import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { operationType, selectDataType } from "../../../../../services/types";
import { TextInput } from "../../../../form/textInput";
import { DateInput } from "../../../../form/date";
import { getvaluesUsingVariables, updateNodeValue } from "../../../../../services/method";
import { Checkboxs } from "../../../../form/checkboxs";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { Switch } from "../../../../form/switch";
import { Pagination } from "../../../../form/pagination";
import { getGoogleAccountToken } from "../../../../auth/method";
import { webURL } from "../../../../../services/API";
import { AuthenticateSelect } from "../../../../auth/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type DriveProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function GoogleDrive({ id, data, setChildMinimize, setChildSettings }: DriveProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'List Files', refId: 'list_files', isActive: true }, 
      { id: 2, name: 'Upload File', refId: 'upload' },
      { id: 3, name: 'Create Folder', refId: 'create_folder' },
      { id: 4, name: 'Delete Folder', refId: 'delete_folder' },
      { id: 5, name: 'Let AI Agent Decide', refId: 'agent_decide'},
   ]);
   const { getNodes, setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { request: requests[0].refId });
      }
      else setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: any) => {
      if (type === 'add') { 
         updateNodeValue(id, setNodes, { connection: data });
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }
   // ---
   const handleSelectRequests = (selectId: number) => {
      const refId = requests.find(obj => obj.id === selectId)?.refId;
      setRequests((requests) =>
         requests.map((request) => {
         if (request.id === selectId) return { ...request, isActive: true };
         else return { ...request, isActive: false };
         })
      );
      updateNodeValue(id, setNodes, { request: refId });
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   };
   // ---
   const handleFetchFolders = async () => {
      if (!data.value?.connection) return;
      updateNodeValue(id, setNodes, { isLoading: true });
      try {
      const token = await getGoogleAccountToken(data.value?.connection, id, setNodes);
      const projectId = new URLSearchParams(window.location.search).get("id");
      const response = await axios.post(
         `${webURL}/blocks/drive/fetch/folders`,
         { projectId: projectId || undefined,  nodeId: id, connection: data.value?.connection },
         token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      if (response.data?.success) {
         const folders = response.data.folders.map((folder: any, index: number) => ({
            id: folder.id,
            name: folder.name,
            isActive: data.value?.folder_id ? folder.id === data.value.folder_id : index === 0,
         }));
         updateNodeValue(id, setNodes, { isLoading: false,  folders, folder_id: data.value?.folder_id || folders[0]?.id || "" });
         return;
      }
      } catch (error) {
         console.error("Fetch Drive folders error:", error);
      }
      updateNodeValue(id, setNodes, { isLoading: false });
   };
   // ---
   const hanedleButtons = async (type: "fetch" | "page" | "download" | "delete", value?: any) => {
      try {
         updateNodeValue(id, setNodes, { isLoading: true });
         const token = await getGoogleAccountToken( data.value?.connection, id, setNodes);

         if (type === "fetch" || type === "page") {
            let request = {
               folder_id: data.value?.folder_id,
               title: data.value?.title,
               extension: data.value?.extension,
               modified_time: data.value?.modified_time,
            };
            // Variables system
            request = { ...request, ...getvaluesUsingVariables(request, getNodes()) };
            // @ts-ignore
            for (let key in request) {
               if (!request[key as keyof typeof request]) { delete request[key as keyof typeof request]; }
            }
            if (request.modified_time) request.modified_time = new Date(data.value.modified_time).toISOString();
            const response = (await axios.post(`/storage/gdrive/list_files?token=${token}&page=${type === "page" ? value : 1}`, request)).data;

            updateNodeValue(id, setNodes, {
               isLoading: false,
               files: response.files.map((file: any) => ({ id: file.id, name: file.name, type: file.mimeType })),
               page: { folder_id: request.folder_id, current_page: response.current_page, total_pages: response.total_pages },
            });
         } else if (type === "download") {
            const filter = data.value.files.filter((file: any) => file.isActive && !file.url).map((file: any) => file.id);
            if (filter.length) {
               const fetch = (
                  await axios.post(`/storage/gdrive/download_files?token=${token}`, {
                  file_ids: filter,
               })).data.downloaded_files;
               const reMap = data.value.files.map((file: any) => ({
                  ...file, url: file.url || fetch.find((f: any) => f.id === file.id)?.uploadedUrl || "",
               }));
               updateNodeValue(id, setNodes, { isLoading: false, files: reMap });
            } else {
               updateNodeValue(id, setNodes, { isLoading: false });
            }
         } else if (type === "delete") {
            const filter = data.value.files.filter((file: any) => file.isActive);
            for (const item of filter) {
               await axios.delete(`/storage/gdrive/delete/${item.id}?token=${token}`);
            }
            updateNodeValue(id, setNodes, {
               isLoading: false, files: data.value.files.filter((file: any) => !filter.some((f: any) => f.id === file.id))
            });
         }
      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
         if (type === "delete") hanedleButtons("fetch");
      }
   };
   // ---
   const filesLengthActive = data.value?.files?.filter((item: any) => item.isActive)?.length;
   const isWarning = !data.value?.connection || !data.value?.folder_id;

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </>
         : null}
         { isExpanded ? (
            <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            styleParent={
               data.value?.request === "list_files" && data.value?.files?.length
                  ? { right: "-136.5px" }
                  : {}
            }
            style={{ width: "420px" }}>
            <Styled>
               <AuthenticateSelect
                  type="google"
                  defaultId={data.value?.connection?.id}
                  handleAuthenticate={handleAuthenticate}
                  background={data.background}
               />
               <div className="action">
                  {data.value?.request === "list_files" &&
                  data.value?.connection ? (
                  <>
                     <button
                        className="btn1 withBorder"
                        onClick={(_) => hanedleButtons("fetch")}
                        disabled={data.value?.isLoading}>
                        Fetch Files
                     </button>
                     {!filesLengthActive ?
                        <button
                           style={{ marginLeft: '7.5px' }}
                           className="btn1 withBorder"
                           onClick={handleFetchFolders}
                           disabled={data.value?.isLoading}
                        >
                           Fetch Folders
                        </button>
                     : null}
                     {filesLengthActive ? (
                        <>
                        <button
                           className="btn1 withBorder download"
                           onClick={(_) => hanedleButtons("download")}
                           disabled={data.value?.isLoading}>
                           Download
                        </button>
                        <button
                           className="btn1 withBorder delete"
                           onClick={(_) => hanedleButtons("delete")}
                           disabled={data.value?.isLoading}>
                           Delete
                        </button>
                        </>
                     ) : null}
                  </>
                  ) : null}
               </div>
               <Select
                  title="Request"
                  data={requests}
                  handleSelectItem={handleSelectRequests}
                  isSmall={true}
               />
               {data.value?.request === "create_folder" ? (
                  <TextInput
                  keyRef="folder_title"
                  title="Title"
                  placeholder="folder_title"
                  value={data.value?.folder_title || ""}
                  handleChange={handleChangeInput}
                  />
               ) : (
                  <div style={{ width: "100%",  marginTop: '10px' }}>
                  {data.value?.folders?.length ? (
                     <Select
                        title="Folder"
                        data={data.value.folders.map((folder: any) => ({
                        id: folder.id,
                        name: folder.name || folder.id,
                        isActive: folder.id === data.value?.folder_id,
                        }))}
                        handleSelectItem={(folderId: any) => {
                        updateNodeValue(id, setNodes, {
                           folder_id: folderId,
                           folders: data.value.folders.map((folder: any) => ({
                              ...folder,
                              isActive: folder.id === folderId,
                           })),
                        });
                        }}
                        isSmall
                     />
                  ) : (
                     <TextInput
                        keyRef="folder_id"
                        title="Folder Id"
                        placeholder="folder_id"
                        value={data.value?.folder_id || ""}
                        handleChange={handleChangeInput}
                     />
                  )}
                  {/* <button className="btn1 withBorder" style={{ marginTop: '6px' }} onClick={ handleFetchFolders } disabled={ data.value?.isLoading || !data.value?.connection }>
                     Fetch Folders
                  </button> */}
                  </div>
               )}
               {data.value?.request === "list_files" ? (
                  <>
                  <TextInput
                     keyRef="title"
                     title="Contains"
                     placeholder="text_contains"
                     value={data.value?.title || ""}
                     handleChange={handleChangeInput}
                  />
                  <TextInput
                     keyRef="extension"
                     title="Extension"
                     placeholder="extension"
                     value={data.value?.extension || ""}
                     handleChange={handleChangeInput}
                  />
                  <DateInput
                     title="Modified"
                     value={data.value?.modified_time || ""}
                     handleChange={(value: string) => {
                        updateNodeValue(id, setNodes, { modified_time: value });
                     }}
                  />
                  <Switch
                     title="Start autopilot (with loop)"
                     className="autopilot"
                     value={!!data.value?.isAutopilot}
                     setValue={() =>
                        updateNodeValue(id, setNodes, {
                        isAutopilot: !data.value?.isAutopilot,
                        })
                     }
                  />
                  <p>
                     Please select your filter carefully to avoid downloading
                     incorrect or unintended files!
                  </p>
                  {data.value?.files?.length ? (
                     <div className="filesEdit">
                        <h3
                        className="title"
                        style={{
                           backgroundColor: data.background,
                           color: "#fff",
                        }}>
                        Files ({filesLengthActive}/{data.value.files.length})
                        </h3>
                        <div className="scroll nowheel">
                        <Pagination
                           disabled={data.value?.isLoading}
                           currentPage={data.value.page?.current_page}
                           totalPages={data.value.page?.total_pages}
                           onPageChange={(number) =>
                              hanedleButtons("page", number)
                           }
                           color={data.background}
                        />
                        <Checkboxs
                           data={data.value.files.map((file: any) => ({
                              ...file,
                              isNotActive: !file.url,
                           }))}
                           handelCheckbox={(fileId: any, value?: boolean) => {
                              updateNodeValue(id, setNodes, {
                              files: data.value.files.map((item: any) => {
                                 if (item.id === fileId)
                                    return { ...item, isActive: !value };
                                 else return item;
                              }),
                              });
                           }}
                        />
                        </div>
                     </div>
                  ) : null}
                  </>
               ) : null}
            </Styled>
            </ExpandedBlock>
         ) : null}
      </Main>
   </>);
}